/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Dispatch, SetStateAction, useState, useContext } from 'react';
import { isNil, isEmpty, get } from 'lodash';
import { Form } from 'react-final-form';

import { BlendBridge } from 'blb-types/blendBridge';
import { completeTask } from 'blb-api/tasks';
import { errorMessage } from 'blb-utils/errors';
import { MeContext } from 'blb-contexts/Me';
import { TaskModal } from '../TaskModal';
import { Task } from './Task';
import { Columns } from '../TaskDescription';

export namespace FormTask {
  export interface Props extends Omit<BlendBridge.API.FormTask, 'type'> {
    onSuccess?: () => Promise<any>;
    taskPreOpeningSubheader?: string;
  }
  export type FormValues = BlendBridge.API.FormTaskCompleteMeta;
}
interface HandleFormSubmitParams {
  formValues: FormTask.FormValues;
  handleClose: () => void;
  onSuccess?: () => Promise<any>;
  principalId?: string;
  principalType?: BlendBridge.PrincipalType;
  setError: Dispatch<SetStateAction<string | undefined>>;
  setLoading: (value: boolean) => void;
  taskId?: string;
}

export const handleFormSubmit = async ({
  formValues,
  handleClose,
  onSuccess,
  setError,
  setLoading,
  taskId,
  principalId,
  principalType,
}: HandleFormSubmitParams): Promise<void> => {
  if (!isNil(taskId)) {
    try {
      setLoading(true);
      // hit the external service to complete the task
      const body: { meta?: BlendBridge.API.FormTaskCompleteMeta } = {};
      if (!isEmpty(formValues)) {
        body.meta = formValues;
      }
      // Notary Upload tasks require the user's principalId
      if (principalType === 'notary') {
        body.meta = { ...body.meta, principalId };
      }
      await completeTask(taskId, body);
      // The following is necessary as for some reason, the Credential Upload modals won't close on
      // their own after completeTask() has run. TODO: pull this out when Credential Upload moves to
      // BSR
      if (principalType === 'notary') {
        handleClose();
        setLoading(false);
      }
      if (onSuccess) {
        // onSuccess will re-initialize loading the loan e.g. to hide this now-completed task;
        // don't call any setState functions after onSuccess() because the component may have unmounted
        await onSuccess();
      } else {
        // we don't use a `finally` block to set state because onSuccess will have unmounted the component
        setLoading(false);
        handleClose();
      }
    } catch (err) {
      const serviceErrorMessage = get(err, 'response.data.error.serviceErrorMessage');
      if (serviceErrorMessage && serviceErrorMessage !== '') {
        setError(serviceErrorMessage);
        setLoading(false);
        return;
      }

      setError(get(err, 'response.data.error.message', errorMessage('SENDING_FORM', principalType)));
      setLoading(false);
    }
  }
};

export const FormTask: React.FC<FormTask.Props> = ({
  buttonLabel,
  columnedTaskDescription,
  disabledReason,
  displayType,
  isDisabled,
  isModalSubmitButtonDisabled,
  modalSubmitButtonDisabledReason,
  modalSubmitButtonLabel,
  onSuccess,
  taskDescription,
  taskHeader,
  taskSubheader,
  taskId,
  taskPreOpeningSubheader,
}: FormTask.Props) => {
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isModalSubmitDisabled, setIsModalSubmitDisabled] = useState<boolean>(isModalSubmitButtonDisabled || false);
  const me = useContext(MeContext);

  const handleClose = () => {
    setError(undefined);
    setModalOpen(false);
    setIsModalSubmitDisabled(false);
  };

  // TODO: Remove this line once we implement all tasks to use columnedTaskDescription
  const columns = columnedTaskDescription || (taskDescription && [taskDescription]) || [];

  const onSubmit = (formValues: FormTask.FormValues) =>
    handleFormSubmit({
      formValues,
      handleClose,
      onSuccess,
      principalId: me?.principalId,
      principalType: me?.principalType,
      setError,
      setLoading,
      taskId,
    });

  return (
    <Task
      buttonLabel={buttonLabel}
      disabledReason={disabledReason}
      displayType={displayType}
      isDisabled={isDisabled}
      onClick={() => setModalOpen(true)}
      taskHeader={taskHeader}
      taskId={taskId}
      taskPreOpeningSubheader={taskPreOpeningSubheader}
      type={BlendBridge.TaskTypes.FORM}
    >
      <Form onSubmit={onSubmit}>
        {({ form, handleSubmit }) => {
          return (
            <TaskModal
              disabledReason={modalSubmitButtonDisabledReason}
              divideFooter={true}
              error={error}
              handleClose={handleClose}
              handleSubmit={handleSubmit}
              isLoading={isLoading}
              isOpen={isModalOpen}
              subTitle={taskSubheader}
              submitButtonDisabled={isModalSubmitDisabled}
              submitButtonLabel={modalSubmitButtonLabel}
              taskId={taskId}
              title={taskHeader}
            >
              <Columns columns={columns} form={form} />
            </TaskModal>
          );
        }}
      </Form>
    </Task>
  );
};
